import styled, { keyframes, css } from 'styled-components';
import theme from '../theme';

// ─── Page wrapper ────────────────────────────────────────────────────────────

export const PageWrapper = styled.div`
  font-family: ${theme.typography.fontFamily};
  min-height: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.lg};
`;

export const PageTitle = styled.h1`
  font-size: ${theme.typography.h1.size};
  font-weight: ${theme.typography.h1.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const PageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const LastRefreshedLabel = styled.span`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textMuted};
`;

export const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: ${theme.radii.sm};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.textSecondary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surfaceHover};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Error banner ─────────────────────────────────────────────────────────────

export const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.danger};
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.danger};
  font-size: ${theme.typography.small.size};
  margin-bottom: ${theme.spacing.md};
`;

// ─── Pipeline Breadcrumb ─────────────────────────────────────────────────────

export const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  overflow-x: auto;
  padding: ${theme.spacing.sm} 0 ${theme.spacing.md} 0;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

export const BreadcrumbSeparator = styled.span`
  color: ${theme.colors.border};
  font-size: 16px;
  flex-shrink: 0;
  user-select: none;
`;

export const BreadcrumbDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${theme.colors.border};
  flex-shrink: 0;
  margin: 0 ${theme.spacing.xs};
`;

// Selected state variants — color signals meaning when active
const pillSelectedAttention = css`
  background: ${theme.colors.warning};
  border-color: ${theme.colors.warning};
  color: #fff;
  &:hover { background: #d97706; }
`;

const pillSelectedDanger = css`
  background: ${theme.colors.danger};
  border-color: ${theme.colors.danger};
  color: #fff;
  &:hover { background: #b91c1c; }
`;

const pillSelectedDefault = css`
  background: ${theme.colors.primary};
  border-color: ${theme.colors.primary};
  color: #fff;
  &:hover { background: ${theme.colors.primaryDark}; }
`;

// Unselected — all pills look the same neutral chip
const pillUnselected = css`
  background: #fff;
  border-color: ${theme.colors.border};
  color: ${theme.colors.text};
  &:hover { border-color: #94a3b8; }
`;

const pillUnselectedEmpty = css`
  background: #fff;
  border-color: ${theme.colors.border};
  color: ${theme.colors.textMuted};
  &:hover { border-color: #94a3b8; }
`;

export const StagePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all ${theme.transitions.fast};

  ${({ $variant, $selected }) => {
    if ($selected) {
      if ($variant === 'attention') return pillSelectedAttention;
      if ($variant === 'danger')    return pillSelectedDanger;
      return pillSelectedDefault;
    }
    if ($variant === 'empty') return pillUnselectedEmpty;
    return pillUnselected;
  }}
`;

export const PillCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: inherit;
  opacity: 0.7;
`;

// ─── Table ────────────────────────────────────────────────────────────────────

export const TableCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
`;

export const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 28px 110px 1fr 120px 110px 70px 1fr 90px;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.background};
  border-bottom: 1px solid ${theme.colors.border};
`;

export const TableHeaderCell = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textMuted};
  user-select: none;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xxl} ${theme.spacing.lg};
  gap: ${theme.spacing.sm};
  color: ${theme.colors.textMuted};
`;

export const EmptyStateIcon = styled.div`
  font-size: 32px;
  margin-bottom: ${theme.spacing.xs};
`;

export const EmptyStateText = styled.p`
  font-size: ${theme.typography.body.size};
  margin: 0;
`;

// ─── Table Row ─────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const RowWrapper = styled.div`
  border-bottom: 1px solid ${theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

export const RowMain = styled.div`
  display: grid;
  grid-template-columns: 28px 110px 1fr 120px 110px 70px 1fr 90px;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  cursor: pointer;
  transition: background ${theme.transitions.fast};
  background: ${({ $expanded }) => $expanded ? theme.colors.background : theme.colors.surface};

  &:hover {
    background: ${theme.colors.surfaceHover};
  }
`;

export const ChevronCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textMuted};
  transition: transform ${theme.transitions.fast};
  transform: ${({ $expanded }) => $expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  font-size: 12px;
`;

export const IdCell = styled.div`
  font-size: ${theme.typography.small.size};
  font-family: 'Courier New', monospace;
  color: ${theme.colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MeetingCell = styled.div`
  overflow: hidden;
`;

export const MeetingTitle = styled.div`
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  color: ${theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MeetingUrl = styled.a`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.primary};
  text-decoration: none;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover { text-decoration: underline; }
`;

export const AgeCell = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
`;

export const IssueCell = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${theme.radii.sm};
  border: none;
  background: transparent;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  font-size: 14px;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.background};
    color: ${theme.colors.text};
  }
`;

// ─── Stage badge / Status badge ───────────────────────────────────────────────

export const StageBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: ${theme.radii.full};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  ${({ $stage }) => {
    switch ($stage) {
      case 'approval_pending': return `background:${theme.colors.warningLight};color:#92400e;`;
      case 'needs_triage':     return `background:${theme.colors.warningLight};color:#92400e;`;
      case 'failed':           return `background:${theme.colors.dangerLight};color:#991b1b;`;
      case 'delivered':        return `background:${theme.colors.successLight};color:#065f46;`;
      case 'email_sent':       return `background:${theme.colors.successLight};color:#065f46;`;
      case 'recording':        return `background:${theme.colors.infoLight};color:#1e40af;`;
      case 'analyzing':        return `background:#f5f3ff;color:#5b21b6;`;
      default:                 return `background:${theme.colors.background};color:${theme.colors.textSecondary};`;
    }
  }}
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: ${theme.radii.full};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  ${({ $variant }) => {
    switch ($variant) {
      case 'pending':  return `background:#0f2b3d;color:#fff;`;
      case 'high':     return `background:${theme.colors.danger};color:#fff;`;
      case 'active':   return `background:${theme.colors.primary};color:#fff;`;
      case 'done':     return `background:${theme.colors.success};color:#fff;`;
      default:         return `background:${theme.colors.border};color:${theme.colors.textSecondary};`;
    }
  }}
`;

// ─── Overflow (⋮) Menu ────────────────────────────────────────────────────────

export const OverflowMenuWrapper = styled.div`
  position: relative;
`;

export const OverflowMenu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 180px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.md};
  z-index: 100;
  overflow: hidden;
  animation: ${fadeIn} ${theme.transitions.fast};
`;

export const OverflowMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: 10px ${theme.spacing.md};
  border: none;
  background: none;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  color: ${({ $danger }) => $danger ? theme.colors.danger : theme.colors.text};
  cursor: pointer;
  text-align: left;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${({ $danger }) => $danger ? theme.colors.dangerLight : theme.colors.background};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ─── Expanded panel ───────────────────────────────────────────────────────────

export const ExpandedPanel = styled.div`
  background: ${theme.colors.background};
  border-top: 1px solid ${theme.colors.border};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  animation: ${fadeIn} ${theme.transitions.fast};
`;

export const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols || 3}, 1fr)`};
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};

  ${theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const PanelField = styled.div``;

export const PanelLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textMuted};
  margin-bottom: 3px;
`;

export const PanelValue = styled.div`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  word-break: break-word;
`;

export const PanelLink = styled.a`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.primary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

export const PanelSection = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

export const PanelSectionTitle = styled.div`
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.xs};
  border-bottom: 1px solid ${theme.colors.border};
`;

export const ParticipantList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

export const ParticipantChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: ${theme.radii.full};
  font-size: 12px;
  background: ${({ $multiply }) => $multiply ? theme.colors.primaryGhost : theme.colors.background};
  border: 1px solid ${({ $multiply }) => $multiply ? theme.colors.primary : theme.colors.border};
  color: ${({ $multiply }) => $multiply ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ $multiply }) => $multiply ? 600 : 400};
`;

// ─── Panel action buttons ─────────────────────────────────────────────────────

export const PanelActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border-radius: ${theme.radii.sm};
  border: none;
  background: ${theme.colors.primary};
  color: #fff;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover:not(:disabled) { background: ${theme.colors.primaryDark}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border-radius: ${theme.radii.sm};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover:not(:disabled) { border-color: ${theme.colors.primary}; color: ${theme.colors.primary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const DangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border-radius: ${theme.radii.sm};
  border: none;
  background: ${theme.colors.danger};
  color: #fff;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover:not(:disabled) { background: #dc2626; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ─── Form elements (used in TriagePanel) ─────────────────────────────────────

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols === 2 ? '1fr 1fr' : '1fr'};
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};

  ${theme.media.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const FormField = styled.div``;

export const FormLabel = styled.label`
  display: block;
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  margin-bottom: 5px;
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border-radius: ${theme.radii.sm};
  border: 1.5px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  appearance: none;
  cursor: pointer;
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryGhost};
  }

  &:disabled { background: ${theme.colors.background}; cursor: not-allowed; }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border-radius: ${theme.radii.sm};
  border: 1.5px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  resize: vertical;
  min-height: 72px;
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryGhost};
  }
`;

export const FormErrorText = styled.div`
  font-size: 12px;
  color: ${theme.colors.danger};
  margin-top: 4px;
`;

// ─── Processing Timeline ──────────────────────────────────────────────────────

export const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  position: relative;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 24px;
    width: 2px;
    height: calc(100% - 8px);
    background: ${({ $done }) => $done ? theme.colors.success : theme.colors.border};
  }
`;

export const TimelineDot = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;

  ${({ $status }) => {
    switch ($status) {
      case 'done':    return `background:${theme.colors.success};color:#fff;border:2px solid ${theme.colors.success};`;
      case 'current': return `background:${theme.colors.primary};color:#fff;border:2px solid ${theme.colors.primary};`;
      case 'failed':  return `background:${theme.colors.danger};color:#fff;border:2px solid ${theme.colors.danger};`;
      default:        return `background:${theme.colors.surface};color:${theme.colors.textMuted};border:2px solid ${theme.colors.border};`;
    }
  }}
`;

export const TimelineContent = styled.div`
  padding-bottom: ${theme.spacing.md};
  flex: 1;
`;

export const TimelineLabel = styled.div`
  font-size: ${theme.typography.body.size};
  font-weight: 500;
  color: ${({ $dim }) => $dim ? theme.colors.textMuted : theme.colors.text};
`;

export const TimelineTime = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  margin-top: 2px;
`;

// ─── Raw data modal / History popover ────────────────────────────────────────

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 43, 61, 0.4);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${keyframes`from{opacity:0}to{opacity:1}`} ${theme.transitions.fast};
`;

export const ModalCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.lg};
  width: min(760px, 90vw);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
`;

export const ModalTitle = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.lg};
`;

export const ModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.textMuted};
  font-size: 18px;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.background};
    color: ${theme.colors.text};
  }
`;

export const RawDataPre = styled.pre`
  font-size: 12px;
  line-height: 1.6;
  color: ${theme.colors.text};
  background: ${theme.colors.background};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.sm};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const SkeletonRow = styled.div`
  height: 52px;
  background: linear-gradient(90deg, ${theme.colors.background} 25%, ${theme.colors.borderLight} 50%, ${theme.colors.background} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-bottom: 1px solid ${theme.colors.borderLight};
`;
